import { createTimedSignal, resolveRequestTimeout } from '~/utils/requestTimeout'

export type ApiClientErrorKind =
  | 'network'
  | 'timeout'
  | 'cancelled'
  | 'rate-limit'
  | 'http'
  | 'unknown'

export interface ApiClientError {
  from: 'api' | 'query' | 'mutation'
  type: 'onRequestError' | 'onResponseError'
  kind: ApiClientErrorKind
  code?: string
  message: string
  statusCode?: number
  cause?: unknown
  isApiClientError: true
  request?: {
    url?: string
    method?: string
  }
  response?: {
    status?: number
  }
  data?: unknown
  result?: unknown
}

function classifyApiError(
  error: unknown,
  statusCode?: number,
  errorCode?: string,
): ApiClientErrorKind {
  if (statusCode === 408 || statusCode === 504 || errorCode?.endsWith('_TIMEOUT')) return 'timeout'
  if (statusCode === 429) return 'rate-limit'

  let err: Error | null = null
  if (error instanceof Error) {
    err = error
  } else if (error && typeof error === 'object' && 'error' in error) {
    const inner = (error as { error: unknown }).error
    if (inner instanceof Error) {
      err = inner
    }
  }

  if (!err) {
    return statusCode ? 'http' : 'unknown'
  }

  if (err.name === 'AbortError' || err.message === 'The user aborted a request.') {
    return 'cancelled'
  }

  if (err.name === 'TimeoutError' || err.message?.toLowerCase().includes('timeout')) {
    return 'timeout'
  }

  const errMsg = err.message
  if (
    errMsg === 'Failed to fetch' ||
    errMsg === 'fetch failed' ||
    errMsg === 'NetworkError when attempting to fetch resource.' ||
    errMsg === 'Load failed'
  ) {
    return 'network'
  }

  return statusCode ? 'http' : 'unknown'
}

export const useApiClient = async <T>(
  request: Parameters<typeof $fetch<T>>[0],
  opts?: Parameters<typeof $fetch<T>>[1],
) => {
  const runtimeConfig = useRuntimeConfig()
  const { correlationId } = useAuthStore()
  const header = runtimeConfig.public.FAKE_HEADER
  const baseUrl = runtimeConfig.public.API_BASE_URL
  const requestTimeout = resolveRequestTimeout(request, opts?.timeout)
  const { signal, cleanup } = createTimedSignal(opts?.signal, requestTimeout)

  try {
    return await $fetch<T>(request, {
      baseURL: baseUrl,
      onRequestError: (ctx) => {
        const statusCode = ctx.response?.status
        const rawError = ctx.error
        const kind = classifyApiError(signal?.reason ?? rawError, statusCode)

        throw {
          from: 'api',
          type: 'onRequestError',
          kind,
          message: rawError?.message ?? '',
          statusCode,
          cause: rawError,
          isApiClientError: true,
          request: {
            url: ctx.request?.toString(),
            method: ctx.options?.method ?? 'GET',
          },
        } as ApiClientError
      },
      onResponseError: (ctx) => {
        if (ctx.response.status !== 400) {
          const statusCode = ctx.response?.status
          const rawError = ctx.error
          const responseData = ctx.response?._data as { code?: string; error?: string } | undefined
          const kind = classifyApiError(rawError, statusCode, responseData?.code)

          throw {
            from: 'api',
            type: 'onResponseError',
            kind,
            code: responseData?.code,
            message: rawError?.message ?? responseData?.error ?? '',
            statusCode,
            cause: rawError,
            isApiClientError: true,
            request: {
              url: ctx.request?.toString(),
              method: ctx.options?.method ?? 'GET',
            },
            response: {
              status: statusCode,
            },
            data: ctx.response,
            result: ctx.response._data,
          } as ApiClientError
        }
      },
      retry: opts?.retry ?? false,
      headers: {
        ...opts?.headers,
        ...(header ? { 'x-ms-client-principal': header } : {}),
        'X-Correlation-ID': correlationId ?? '',
      },
      ...opts,
      signal,
      // The signal above combines Vue Query cancellation with this timeout.
      timeout: undefined,
    })
  } finally {
    cleanup()
  }
}
